import React, { useState, useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import Button from './Button';
import Badge from './Badge';
import TierBadge from './TierBadge';
import CloseButton from './CloseButton';
import SearchInput from './SearchInput';
import { Checkbox } from '@components/common';
import { RZTable } from './table';
import { TableColumn, ContractState } from '@app-types';
import { getStatusLabel } from '@utils/statusUtils';
import { contractStateToVariant, tierToVariant } from '@utils/badgeMappers';
import { LoadingSpinner } from '@components/common/loading';
import { showToast } from '@utils/toast';
import { joinPersonNames, matchesContractSearch } from '@utils/contractSearch';
import styles from './AddToGroupModal.module.css';

// Generic contract item for display
export interface ContractItem {
  id: number;
  contract_number: string;
  tier: string;
  state: string;
  assessor_name?: string;
  supplier_name?: string;
  owner_names?: string;
  manager_names?: string;
}

interface ScheduleItemContract {
  id: number;
  contract_number: string;
  tier: string;
  state: string;
  supplier_name?: string;
  contract_owners?: Array<{ name?: string | null }>;
  contract_managers?: Array<{ name?: string | null }>;
}

interface ScheduleItemWithContracts {
  contracts: ScheduleItemContract[];
  assessor?: {
    name: string;
  };
}

interface FlatContractItem {
  contract_id: number;
  contract_number: string;
  tier_name: string;
  meeting_status: string;
  assessor_name?: string;
  supplier_name?: string;
  contract_owners?: Array<{ name?: string | null }>;
  contract_managers?: Array<{ name?: string | null }>;
}

// Group metadata for display
export interface GroupMetadata {
  tier: string;
  assessor?: string;
  status: string;
  count?: number;
}

export interface AddToGroupModalProps {
  title?: string;
  groupMetadata: GroupMetadata;
  groupId: number;
  queryKey: string[];
  queryFn: () => Promise<{ data: unknown[] }>;
  onClose: () => void;
  onAdd: (groupId: number, contractIds: number[]) => Promise<void> | void;
  dataTestId?: string;
}

// Column configuration for contracts table
const contractsTableColumns: TableColumn[] = [
  { key: 'checkbox', label: '', width: '50px', defaultVisible: true },
  { key: 'state', label: 'Status', defaultVisible: true },
  { key: 'contract_number', label: 'Contract #', defaultVisible: true },
  { key: 'tier', label: 'Tier', defaultVisible: true },
  { key: 'assessor_name', label: 'Assessor', defaultVisible: true }
];

const AddToGroupModal: React.FC<AddToGroupModalProps> = ({
  title = 'Add Contracts to Group',
  groupMetadata,
  groupId,
  queryKey,
  queryFn,
  onClose,
  onAdd,
  dataTestId = 'add-to-group'
}) => {
  const [selectedContractIds, setSelectedContractIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch compatible contracts from API
  const { data: compatibleData, isLoading } = useQuery({
    queryKey,
    queryFn,
    staleTime: 0,
    gcTime: 0
  });

  // Extract contracts from response (handles both ScheduleItem[] and flat contract arrays)
  const contracts: ContractItem[] = useMemo(() => {
    if (!compatibleData?.data) return [];

    const dataArray = compatibleData.data as unknown[];

    // Type guard for ScheduleItem with contracts
    const isScheduleItemWithContracts = (item: unknown): item is ScheduleItemWithContracts => {
      return typeof item === 'object' && item !== null && 'contracts' in item;
    };

    // Type guard for flat contract item
    const isFlatContractItem = (item: unknown): item is FlatContractItem => {
      return typeof item === 'object' && item !== null && 'contract_id' in item;
    };

    const firstItem = dataArray[0];

    // If data is array of ScheduleItems with nested contracts
    if (isScheduleItemWithContracts(firstItem)) {
      return (dataArray as ScheduleItemWithContracts[]).flatMap((scheduleItem) =>
        scheduleItem.contracts.map((c) => ({
          id: c.id,
          contract_number: c.contract_number,
          tier: c.tier,
          state: c.state,
          assessor_name: scheduleItem.assessor?.name,
          supplier_name: c.supplier_name,
          owner_names: joinPersonNames(c.contract_owners),
          manager_names: joinPersonNames(c.contract_managers)
        }))
      );
    }

    // If data is flat array of contracts
    if (isFlatContractItem(firstItem)) {
      return (dataArray as FlatContractItem[]).map((contract) => ({
        id: contract.contract_id,
        contract_number: contract.contract_number,
        tier: contract.tier_name,
        state: contract.meeting_status,
        assessor_name: contract.assessor_name,
        supplier_name: contract.supplier_name,
        owner_names: joinPersonNames(contract.contract_owners),
        manager_names: joinPersonNames(contract.contract_managers)
      }));
    }

    return [];
  }, [compatibleData]);

  // Filter contracts with debounce
  const filteredContracts = useMemo(() => {
    if (!searchQuery.trim()) {
      return contracts;
    }

    return contracts.filter((contract) => matchesContractSearch(contract, searchQuery));
  }, [searchQuery, contracts]);

  // Render cell content for RZTable
  const renderContractCell = (column: TableColumn, contract: ContractItem) => {
    const isSelected = selectedContractIds.has(contract.id);

    switch (column.key) {
      case 'checkbox':
        return (
          <Checkbox
            checked={isSelected}
            onChange={() => handleToggleContract(contract.id)}
            onClick={(e) => e.stopPropagation()}
            disabled={isSubmitting}
          />
        );
      case 'state': {
        const contractState = contract.state as ContractState;
        return (
          <Badge variant={contractStateToVariant(contractState)}>
            {getStatusLabel(contract.state) || contract.state}
          </Badge>
        );
      }
      case 'contract_number':
        return <span className={styles.contractNumber}>{contract.contract_number}</span>;
      case 'tier':
        return (
          <TierBadge variant={tierToVariant(contract.tier)} className={styles.propertyTier}>
            {contract.tier}
          </TierBadge>
        );
      case 'assessor_name':
        return <span className={styles.assessorName}>{contract.assessor_name || 'Not Assigned'}</span>;
      default:
        return null;
    }
  };

  const handleToggleContract = (contractId: number) => {
    const newSelected = new Set(selectedContractIds);
    if (newSelected.has(contractId)) {
      newSelected.delete(contractId);
    } else {
      newSelected.add(contractId);
    }
    setSelectedContractIds(newSelected);
  };

  const handleApply = async () => {
    if (selectedContractIds.size === 0) return;

    setIsSubmitting(true);
    try {
      await onAdd(groupId, Array.from(selectedContractIds));
    } catch (error) {
      const apiError = error as { response?: { data?: { error?: string } } };
      const message = apiError?.response?.data?.error || 'Failed to add contracts to group. Please try again.';
      showToast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} data-test-id={`${dataTestId}-modal`}>
      {isSubmitting && (
        <div className={styles.modalLoadingOverlay}>
          <div className={styles.modalLoadingSpinner}></div>
        </div>
      )}
      <div className={styles.modal}>
        {isSubmitting && (
          <div className={styles.modalLoadingOverlay}>
            <div className={styles.modalLoadingSpinner}>
              <LoadingSpinner size="medium" />
              <p>Adding contracts to group...</p>
            </div>
          </div>
        )}

        <div className={styles.header}>
          <h3 data-test-id={`${dataTestId}-title`}>{title}</h3>
          <CloseButton onClick={onClose} disabled={isSubmitting} />
        </div>

        <div className={styles.content}>
          {/* Group Properties */}
          <div className={styles.groupInfoBox}>
            <h5>Group Properties:</h5>
            <div className={styles.propertiesRow}>
              <div className={styles.property}>
                <span className={styles.label}>Tier:</span>
                <TierBadge variant={tierToVariant(groupMetadata.tier)} className={styles.propertyTier}>
                  {groupMetadata.tier || 'N/A'}
                </TierBadge>
              </div>
              <div className={styles.property}>
                <span className={styles.label}>Assessor:</span>
                <span className={styles.value}>{groupMetadata.assessor || 'Not Assigned'}</span>
              </div>
              <div className={styles.property}>
                <span className={styles.label}>Status:</span>
                <Badge
                  variant={contractStateToVariant(groupMetadata.status as ContractState)}
                  className={styles.propertyStatus}
                >
                  {getStatusLabel(groupMetadata.status) || groupMetadata.status || 'N/A'}
                </Badge>
              </div>
              {groupMetadata.count !== undefined && (
                <div className={styles.property}>
                  <span className={styles.label}>Contracts in group:</span>
                  <span className={styles.value}>{groupMetadata.count}</span>
                </div>
              )}
            </div>
          </div>

          {/* Instructions and Search Row */}
          <div className={styles.instructionsRow}>
            <p className={styles.instructions}>Select contracts to add (only compatible contracts are enabled):</p>

            {/* Search Input */}
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search number, supplier, owner, manager..."
              disabled={isSubmitting}
            />
          </div>

          {/* Available Contracts Table */}
          <div className={styles.contractsTableContainer} data-test-id={`${dataTestId}-contracts-table`}>
            <RZTable
              data={filteredContracts}
              columns={contractsTableColumns}
              getRowKey={(contract) => contract.id}
              renderCell={renderContractCell}
              onRowClick={(contract) => !isSubmitting && handleToggleContract(contract.id)}
              rowClassName={(contract) => (selectedContractIds.has(contract.id) ? styles.selected : '')}
              state={{
                isLoading,
                isEmpty: filteredContracts.length === 0,
                loadingMessage: 'Loading compatible contracts...'
              }}
              emptyState={
                contracts.length > 0 ? (
                  <RZTable.Empty
                    icon={<AlertCircle size={48} />}
                    title={`No contracts found matching "${searchQuery}"`}
                    description="Try a different search term"
                  />
                ) : (
                  <RZTable.Empty
                    icon={<AlertCircle size={48} />}
                    title="No compatible contracts available to add to this group"
                    description="Contracts must have the same status and assessor"
                  />
                )
              }
            />
          </div>

          {/* Selection Count */}
          {filteredContracts.length > 0 && (
            <div className={styles.selectionCount}>
              {selectedContractIds.size} contract{selectedContractIds.size !== 1 ? 's' : ''} selected
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <Button variant="primary" onClick={handleApply} disabled={selectedContractIds.size === 0 || isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddToGroupModal;
